import { useState, type ReactNode } from "react";
import { useIntl } from "react-intl";
import { Button } from "@/components/design/Button";

interface AdminMasterDetailProps {
  /** Überschrift der Liste, auch als Titel des eingeklappten Streifens. */
  title: string;
  /** Wird als `(n)` neben den Titel gesetzt. Weglassen, wenn keine Zahl trägt. */
  count?: number;
  /** Inhalt der linken Spalte: Aktionen und Einträge. */
  list: ReactNode;
  /** Rechte Spalte. */
  children: ReactNode;
}

// Zweispaltiges Admin-Layout: links eine einklappbare Liste, rechts das
// Detail zum gewählten Eintrag. Eingeklappt bleibt links ein schmaler
// Streifen mit `›` stehen, damit die Liste erreichbar bleibt, ohne dass die
// Spalte Platz kostet.
//
// Aus dem QuestionnaireManager herausgezogen, als die Stammdaten-Teamprofile
// dasselbe Layout brauchten (spark#218). Der Rahmen ist absichtlich dumm: er
// kennt weder Auswahl noch Datenquelle, nur zwei Slots. Alles, was von der
// Auswahl abhängt, gehört in `list` bzw. `children`.
export function AdminMasterDetail({ title, count, list, children }: AdminMasterDetailProps) {
  const intl = useIntl();
  const [listOpen, setListOpen] = useState(true);

  return (
    <div
      className={`grid grid-cols-1 gap-6 @lg:items-start ${
        listOpen
          ? "@lg:grid-cols-[minmax(240px,320px)_minmax(0,1fr)]"
          : "@lg:grid-cols-[auto_minmax(0,1fr)]"
      }`}
    >
      {listOpen ? (
        <aside className="box bg-surface p-4">
          <div className="mb-3 flex items-center justify-between gap-2">
            <span className="text-smycles-body font-medium">
              {title}
              {undefined === count ? null : (
                <span className="ml-1 text-smycles-label text-ink-muted">({count})</span>
              )}
            </span>
            <Button
              variant="icon"
              size="sm"
              onClick={() => setListOpen(false)}
              aria-label={intl.formatMessage({ id: "admin.collapseList", defaultMessage: "Collapse list" })}
            >
              <span className="text-smycles-body leading-none">‹</span>
            </Button>
          </div>
          {list}
        </aside>
      ) : (
        <aside>
          <div className="box bg-surface flex w-10 items-center justify-center py-2 @lg:sticky @lg:top-6">
            <Button
              variant="icon"
              size="sm"
              onClick={() => setListOpen(true)}
              aria-label={intl.formatMessage(
                { id: "admin.expandListNamed", defaultMessage: "Show {title}" },
                { title },
              )}
              title={title}
            >
              <span className="text-smycles-body leading-none">›</span>
            </Button>
          </div>
        </aside>
      )}

      {children}
    </div>
  );
}
